import type { ReactNode } from 'react'
import { AlertTriangle, ImageIcon, MapPin, Phone, User as UserIcon } from 'lucide-react'
import { useCustomer } from '@/hooks/useCustomers'
import type { Customer } from '@/types/customer'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { PermissionGuard } from '@/components/auth/PermissionGuard'
import { CustomerServiceRecordTab } from './CustomerServiceRecordTab'

interface CustomerDetailDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  customerId?: number
}

const genderLabel: Record<number, string> = {
  0: '未知',
  1: '男',
  2: '女',
}

function getContactValue(contacts: Customer['contacts'] = [], key: string) {
  return contacts.find(c => c.key === key)?.value || '-'
}

function Field({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="space-y-1">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="break-words text-sm">{value || '-'}</div>
    </div>
  )
}

function Section({ icon, title, children }: { icon: ReactNode; title: string; children: ReactNode }) {
  return (
    <section className="space-y-3">
      <div className="flex items-center gap-2 text-sm font-semibold">
        {icon}
        <span>{title}</span>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">{children}</div>
    </section>
  )
}

function ImageField({ label, value }: { label: string; value?: string }) {
  if (!value) return <Field label={label} value="-" />

  return (
    <div className="space-y-2">
      <div className="text-xs text-muted-foreground">{label}</div>
      <a href={value} target="_blank" rel="noreferrer" className="block overflow-hidden rounded-md border bg-muted">
        <img src={value} alt={label} className="h-32 w-full object-cover transition-opacity hover:opacity-90" />
      </a>
    </div>
  )
}

export function CustomerDetailDrawer({ open, onOpenChange, customerId }: CustomerDetailDrawerProps) {
  const { data: customer, isLoading } = useCustomer(customerId || 0, open && !!customerId)

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-2xl">
        <SheetHeader>
          <SheetTitle>客户详细信息</SheetTitle>
        </SheetHeader>

        {isLoading ? (
          <div className="mt-6 space-y-4">
            {Array.from({ length: 8 }).map((_, index) => (
              <Skeleton key={index} className="h-6 w-full" />
            ))}
          </div>
        ) : customer ? (
          <div className="mt-6 space-y-6">
            <Tabs defaultValue="info">
              <TabsList>
                <TabsTrigger value="info">基本信息</TabsTrigger>
                <PermissionGuard permission="service_record:list">
                  <TabsTrigger value="service-records">服务记录</TabsTrigger>
                </PermissionGuard>
              </TabsList>
              <TabsContent value="info" className="space-y-6 mt-4">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="truncate text-lg font-semibold">{customer.name}</h2>
                  <Badge variant="secondary">{genderLabel[customer.gender] || '未知'}</Badge>
                  {customer.blacklist && <Badge variant="destructive">黑名单</Badge>}
                </div>
                <p className="mt-1 text-sm text-muted-foreground">客户ID：{customer.id}</p>
              </div>
            </div>

            <Separator />

            <Section icon={<UserIcon className="h-4 w-4" />} title="基础信息">
              <Field label="姓名" value={customer.name} />
              <Field label="性别" value={genderLabel[customer.gender] || '未知'} />
              <Field label="年龄" value={customer.age ? `${customer.age} 岁` : '-'} />
              <Field label="生日" value={customer.birthday ? customer.birthday.slice(0, 10) : '-'} />
              <Field label="生日索引" value={customer.birthday_index} />
              <Field label="抖音号" value={customer.tiktok} />
              <Field label="职业" value={customer.job} />
              <Field label="黑名单" value={customer.blacklist ? '是' : '否'} />
              <Field label="最后服务时间" value={customer.last_service_at ? customer.last_service_at.replace('T', ' ').slice(0, 16) : '-'} />
            </Section>

            <Separator />

            <Section icon={<Phone className="h-4 w-4" />} title="联系方式">
              <Field label="手机号1" value={getContactValue(customer.contacts, 'mobile_1')} />
              <Field label="手机号2" value={getContactValue(customer.contacts, 'mobile_2')} />
              <Field label="微信号1" value={getContactValue(customer.contacts, 'wechat_1')} />
              <Field label="微信号2" value={getContactValue(customer.contacts, 'wechat_2')} />
            </Section>

            <Separator />

            <Section icon={<MapPin className="h-4 w-4" />} title="地址和备注">
              <Field label="省市区" value={[customer.province, customer.city, customer.district].filter(Boolean).join(' / ')} />
              <Field label="详细地址" value={customer.address} />
              <Field label="备注" value={customer.remark} />
              <Field label="家庭情况" value={customer.family} />
            </Section>

            <Separator />

            <Section icon={<ImageIcon className="h-4 w-4" />} title="客户图片">
              <ImageField label="客户图片1" value={customer.pic_1} />
              <ImageField label="客户图片2" value={customer.pic_2} />
            </Section>

            <Separator />

            <Section icon={<AlertTriangle className="h-4 w-4" />} title="锁定信息">
              <Field label="锁定人" value={customer.user_name || '未分配'} />
              <Field label="跟踪人" value={customer.tracker_name || '无'} />
              <Field label="锁定时间" value={customer.locked_at || '-'} />
              <Field label="锁定日期" value={customer.locked_date || '-'} />
              <Field label="创建时间" value={customer.created_at} />
              <Field label="更新时间" value={customer.updated_at} />
            </Section>
              </TabsContent>
              <TabsContent value="service-records" className="mt-4">
                <CustomerServiceRecordTab customerId={customer.id} />
              </TabsContent>
            </Tabs>
          </div>
        ) : (
          <div className="mt-6 text-sm text-muted-foreground">暂无客户详情</div>
        )}
      </SheetContent>
    </Sheet>
  )
}
